import React, { useState } from 'react';
import { ArrowLeftIcon, DocumentCheckIcon, PrinterIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';

const DocumentPreview = ({ documentType, formData, onBack, onSave, viewMode = false, onRefresh = null }) => {
  const { companySettings } = useCompanySettings();
  const [isSaving, setIsSaving] = useState(false);
  
  // Format currency in DA
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ').format(amount) + ' DA';
  };

  // Payment method mapping
  const getPaymentMethodLabel = (method) => {
    const methodMap = {
      'virement': 'Virement bancaire',
      'cheque': 'Chèque',
      'especes': 'Espèces',
      'carte': 'Carte bancaire',
      'effet': 'Effet de commerce',
      'traite': 'Traite',
      'compensation': 'Compensation',
      'prélèvement': 'Prélèvement automatique',
      'mixte': 'Plusieurs modes',
      'crédit_documentaire': 'Crédit documentaire',
      'crédit_compte': 'Crédit sur compte client',
      'acompte': 'Acompte + solde',
      'echeances': 'Paiement échelonné',
      'livraison': 'À la livraison'
    };
    return methodMap[method] || method;
  };

  // Generate item codes like in FACTURATION DEMO
  const generateItemCode = (item, index) => {
    if (item.productId) {
      // Use actual product ID if available
      return item.productId;
    }
    
    // Generate based on description keywords
    const description = (item.description || '').toLowerCase();
    let prefix = 'GEN';
    
    if (description.includes('matériel') || description.includes('hardware')) {
      prefix = 'MAT';
    } else if (description.includes('logiciel') || description.includes('software')) {
      prefix = 'LOG';
    } else if (description.includes('service') || description.includes('consultation')) {
      prefix = 'SRV';
    } else if (description.includes('formation') || description.includes('training')) {
      prefix = 'FOR';
    } else if (description.includes('maintenance')) {
      prefix = 'MNT';
    }
    
    return `${prefix}-${String(index + 1).padStart(3, '0')}`;
  };

  // Format bank account with bold prefix like in FACTURATION DEMO
  const formatBankAccount = (account) => {
    if (!account) return 'N/A';
    const parts = account.split(' ');
    if (parts.length < 2) return account;
    
    // Find bank name (CPA, BADR, BNA, etc.)
    const bankIndex = parts.findIndex(part => 
      ['CPA', 'BADR', 'BNA', 'BEA', 'BDL', 'CNEP', 'AGB'].includes(part)
    );
    
    if (bankIndex === -1) {
      // If no known bank name, take the last word
      const bankPrefix = parts[parts.length - 1];
      const accountNumber = parts.slice(0, -1).join(' ');
      return `${accountNumber} ${bankPrefix}`;
    }
    
    // Otherwise, format properly
    const accountNumber = parts.slice(0, bankIndex).join(' ');
    const bankName = parts[bankIndex];
    const location = parts.slice(bankIndex + 1).join(' ');
    
    return `${accountNumber} ${bankName} ${location}`;
  };

  // Generate and download PDF
  const generatePDF = async () => {
    try {
      const documentElement = document.querySelector('.document-preview > div:nth-child(2) > div');
      
      if (!documentElement) {
        console.error('Document element not found');
        return;
      }

      // Hide the action bar temporarily
      const actionBar = document.querySelector('.document-preview > div:first-child');
      if (actionBar) {
        actionBar.style.display = 'none';
      }

      // Generate canvas from HTML
      const canvas = await html2canvas(documentElement, {
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: documentElement.scrollWidth,
        height: documentElement.scrollHeight
      });

      // Restore action bar
      if (actionBar) {
        actionBar.style.display = 'flex';
      }

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit A4
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;

      // Center the image on the page
      const x = (pdfWidth - scaledWidth) / 2;
      const y = (pdfHeight - scaledHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      
      // Generate filename
      const documentTitle = getDocumentTitle();
      const fileName = `${documentTitle}_${formData.number || 'DOCUMENT'}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Download PDF
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Convert numbers to French words (enhanced version)
  const numberToWords = (num) => {
    if (num === 0) return 'zéro';
    
    const ones = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
    const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix'];
    const hundreds = ['', 'cent', 'deux cents', 'trois cents', 'quatre cents', 'cinq cents', 'six cents', 'sept cents', 'huit cents', 'neuf cents'];
    
    if (num < 10) return ones[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) {
      const ten = Math.floor(num / 10);
      const one = num % 10;
      return tens[ten] + (one ? '-' + ones[one] : '');
    }
    if (num < 1000) {
      const hundred = Math.floor(num / 100);
      const remainder = num % 100;
      return hundreds[hundred] + (remainder ? ' ' + numberToWords(remainder) : '');
    }
    if (num < 1000000) {
      const thousand = Math.floor(num / 1000);
      const remainder = num % 1000;
      return numberToWords(thousand) + ' mille' + (remainder ? ' ' + numberToWords(remainder) : '');
    }
    
    return num.toString();
  };

  const documentTitles = {
    'invoice': 'FACTURE',
    'delivery-note': 'BON DE LIVRAISON',
    'purchase-order': 'BON DE COMMANDE',
    'reception-note': 'BON DE RÉCEPTION'
  };

  const getDocumentTitle = () => {
    return documentTitles[documentType.id] || documentType.title?.toUpperCase() || 'DOCUMENT';
  };

  // Debounced save handler to prevent multiple rapid saves
  const handleSave = async () => {
    if (isSaving) {
      return; // Prevent multiple saves
    }
    
    setIsSaving(true);
    try {
      await onSave();
    } catch (error) {
      console.error('Error saving document:', error);
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="document-preview" data-document-type={documentType.id} style={{ padding: 0, minHeight: '100vh', background: 'white', position: 'relative' }}>
      {/* Action Bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 200, background: 'white', borderBottom: '1px solid #e2e8f0', padding: '1rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
        <button 
          onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', border: '2px solid #64748b', borderRadius: '0.5rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s ease', background: '#64748b', color: 'white', textDecoration: 'none' }}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          {viewMode ? 'Retour' : 'Retour à l\'édition'}
        </button>
        
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          {viewMode && onRefresh && (
            <button 
              onClick={onRefresh}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', border: '2px solid #6b7280', borderRadius: '0.5rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s ease', background: 'transparent', color: '#6b7280', textDecoration: 'none' }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px' }} />
              Actualiser
            </button>
          )}
          <button 
            onClick={generatePDF}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', border: '2px solid #10b981', borderRadius: '0.5rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', transition: 'all 0.2s ease', background: '#10b981', color: 'white', textDecoration: 'none' }}
          >
            <PrinterIcon style={{ width: '16px', height: '16px' }} />
            Télécharger PDF
          </button>
          {!viewMode && (
          <button 
              onClick={handleSave}
              disabled={isSaving}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                padding: '0.75rem 1.5rem', 
                border: `2px solid ${isSaving ? '#64748b' : '#3b82f6'}`, 
                borderRadius: '0.5rem', 
                fontWeight: 600, 
                fontSize: '0.875rem', 
                cursor: isSaving ? 'not-allowed' : 'pointer', 
                transition: 'all 0.2s ease', 
                background: isSaving ? '#64748b' : '#3b82f6', 
                color: 'white', 
                textDecoration: 'none',
                opacity: isSaving ? 0.7 : 1
              }}
          >
            <DocumentCheckIcon style={{ width: '16px', height: '16px' }} />
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
          )}
        </div>
      </div>

      {/* Document Container */}
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', padding: '1.5rem', background: 'white', minHeight: '100vh' }}>
        <div style={{ width: '210mm', minHeight: '297mm', maxHeight: 'none', background: 'white', padding: '15mm', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 20px 25px rgba(0, 0, 0, 0.05)', borderRadius: '0.5rem', position: 'relative', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif', fontSize: '14px', lineHeight: 1.4, marginBottom: '2rem' }}>
          
          {/* Header with new design */}
          <div style={{ width: '100%', background: 'white', padding: '8px 0', marginBottom: 0, marginTop: '-1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginBottom: '0.5rem', position: 'relative', padding: '0 8px' }}>
              <div style={{ position: 'absolute', left: '8px', top: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '80px' }}>
                <div style={{ marginBottom: 0 }}>
                  <div style={{ width: '80px', height: '80px', background: companySettings.logo ? 'transparent' : 'linear-gradient(135deg, #3b82f6, #1e40af)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem', borderRadius: companySettings.logo ? '0' : '50%', border: companySettings.logo ? 'none' : '2px solid #3b82f6' }}>
                    {companySettings.logo ? (
                      <img 
                        src={companySettings.logo} 
                        alt="Logo" 
                        style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                      />
                    ) : (
                    <div style={{ color: 'white', fontSize: '0.625rem', fontWeight: 600, textAlign: 'center' }}>
                      LOGO
                    </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ textAlign: 'center', width: '100%', paddingTop: '0.5rem', position: 'relative' }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '2px', marginTop: '-4px' }}>{companySettings.companyName}</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1e40af', marginBottom: '3px', direction: 'rtl' }}>{companySettings.companyNameArabic}</div>
                <div style={{ fontSize: '0.95rem', color: '#000000', marginBottom: '0.2rem', fontWeight: 600 }}>{companySettings.description}</div>
                <div style={{ fontSize: '0.9rem', color: '#000000', fontStyle: 'italic', marginBottom: '0.5rem', fontWeight: 600 }}>{companySettings.descriptionSubtitle}</div>
            
                <div style={{ width: '80%', height: '1px', background: '#e2e8f0', margin: '0.5rem auto', marginTop: '0.25rem' }}></div>
            
                <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#000', margin: '0.25rem 0', lineHeight: 1.4 }}>
                    <span><strong>ARTICLE N° :</strong> {companySettings.articleNumber} - <strong>NIF N° :</strong> {companySettings.nifNumber} - <strong>NIS N° :</strong> {companySettings.nisNumber} - <strong>RC N° :</strong> {companySettings.rcNumber}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#000', margin: '0.25rem 0', lineHeight: 1.4 }}>
                    <span><strong>COMPTES BANCAIRE N° :</strong> {formatBankAccount(companySettings.bankAccount1)}{companySettings.bankAccount2 ? ` | ${formatBankAccount(companySettings.bankAccount2)}` : ''}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ width: '100%', height: '2px', background: '#3b82f6', marginTop: '0.5rem', clear: 'both' }}></div>
          </div>
          
          {/* Document Title Section */}
          <div style={{ textAlign: 'center', margin: '0 0 0.5rem 0', padding: 0 }}>
            <h2 style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1e40af', margin: 0, textTransform: 'uppercase', letterSpacing: '0.1em', display: 'inline-block', paddingBottom: '0.2rem' }}>{getDocumentTitle()}</h2>
          </div>

          {/* Document Information Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem', fontSize: '0.875rem' }}>
            <div>
              <div style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600, color: '#1e40af' }}>{getDocumentTitle()} N°:</span>
                <span style={{ marginLeft: '0.5rem', fontWeight: 'bold', color: '#000' }}>{formData.number}</span>
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <span style={{ fontWeight: 600, color: '#1e40af' }}>Alger le:</span>
                <span style={{ marginLeft: '0.5rem', color: '#000' }}>{new Date(formData.date).toLocaleDateString('fr-FR')}</span>
              </div>
              {/* Document-specific fields */}
              {documentType.id === 'invoice' && (
                <>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e40af' }}>Paiement:</span>
                    <span style={{ marginLeft: '0.5rem', color: '#000' }}>{getPaymentMethodLabel(formData.paymentMethod)}</span>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e40af' }}>Client N°:</span>
                    <span style={{ marginLeft: '0.5rem', color: '#000' }}>CLI-001</span>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e40af' }}>Échéance:</span>
                    <span style={{ marginLeft: '0.5rem', color: '#000' }}>{formData.dueDate ? new Date(formData.dueDate).toLocaleDateString('fr-FR') : new Date(new Date(formData.date).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR')}</span>
                  </div>
                  {formData.salesPerson && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: '#1e40af' }}>Commercial:</span>
                      <span style={{ marginLeft: '0.5rem', color: '#000' }}>{formData.salesPerson}</span>
                    </div>
                  )}
                </>
              )}

              {documentType.id === 'delivery-note' && (
                <>
                  {formData.deliveryDate && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: '#1e40af' }}>Date de livraison:</span>
                      <span style={{ marginLeft: '0.5rem', color: '#000' }}>{new Date(formData.deliveryDate).toLocaleDateString('fr-FR')}</span>
                    </div>
                  )}
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e40af' }}>Client N°:</span>
                    <span style={{ marginLeft: '0.5rem', color: '#000' }}>CLI-001</span>
                  </div>
                </>
              )}

              {documentType.id === 'purchase-order' && (
                <>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e40af' }}>Fournisseur N°:</span>
                    <span style={{ marginLeft: '0.5rem', color: '#000' }}>FOU-001</span>
                  </div>
                  {formData.deliveryDate && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: '#1e40af' }}>Date livraison souhaitée:</span>
                      <span style={{ marginLeft: '0.5rem', color: '#000' }}>{new Date(formData.deliveryDate).toLocaleDateString('fr-FR')}</span>
                    </div>
                  )}
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e40af' }}>Conditions paiement:</span>
                    <span style={{ marginLeft: '0.5rem', color: '#000' }}>{getPaymentMethodLabel(formData.paymentMethod)}</span>
                  </div>
                  {formData.issuedBy && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: '#1e40af' }}>Responsable:</span>
                      <span style={{ marginLeft: '0.5rem', color: '#000' }}>{formData.issuedBy}</span>
                    </div>
                  )}
                </>
              )}

              {documentType.id === 'reception-note' && (
                <>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 600, color: '#1e40af' }}>Fournisseur N°:</span>
                    <span style={{ marginLeft: '0.5rem', color: '#000' }}>FOU-001</span>
                  </div>
                  {formData.issuedBy && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: 600, color: '#1e40af' }}>Réceptionné par:</span>
                      <span style={{ marginLeft: '0.5rem', color: '#000' }}>{formData.issuedBy}</span>
                    </div>
                  )}
                </>
              )}

              {/* Delivery Address - as additional field */}
              {formData.deliveryAddress && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600, color: '#1e40af' }}>Adresse de livraison:</span>
                  <span style={{ marginLeft: '0.5rem', color: '#000' }}>{formData.deliveryAddress}</span>
                </div>
              )}

              {/* Conditions - as additional field */}
              {formData.conditions && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600, color: '#1e40af' }}>
                    {documentType.id === 'purchase-order' ? 'Conditions commande:' :
                     documentType.id === 'delivery-note' ? 'Conditions livraison:' :
                     documentType.id === 'reception-note' ? 'Conditions réception:' :
                     'Conditions particulières:'}
                  </span>
                  <span style={{ marginLeft: '0.5rem', color: '#000' }}>{formData.conditions}</span>
                </div>
              )}

              {/* Observations - as additional field */}
              {formData.notes && (
                <div style={{ marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 600, color: '#1e40af' }}>Observations:</span>
                  <span style={{ marginLeft: '0.5rem', color: '#1e40af' }}>{formData.notes}</span>
                </div>
              )}
            </div>
            
            {/* Client/Supplier Information */}
            <div style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e40af', margin: '0 0 1rem 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {(documentType.id === 'purchase-order' || documentType.id === 'reception-note') ? 'FOURNISSEUR' : 'CLIENT'}
              </h3>
              <div>
                <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#374151', fontWeight: 'bold' }}>{formData.contactName || 'Non spécifié'}</p>
                <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#374151' }}>{formData.contactEmail || ''}</p>
                <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#374151' }}>{formData.contactPhone || ''}</p>
                <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: '#374151' }}>{formData.contactAddress || ''}</p>
              </div>
            </div>
          </div>



          {/* Items Table */}
          <div className="items-table-section" style={{ margin: '1rem 0 1rem 0', width: '100%', maxHeight: 'calc(100% - 180mm)', overflow: 'visible' }}>
            <table className="items-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem', marginBottom: '1rem', tableLayout: 'fixed', maxHeight: '80mm' }}>
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#f8f9fa', border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#000000', width: '5%' }}>N°</th>
                  <th style={{ backgroundColor: '#f8f9fa', border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#000000', width: '15%' }}>Item</th>
                  <th style={{ backgroundColor: '#f8f9fa', border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#000000', width: '35%' }}>Désignation</th>
                  <th style={{ backgroundColor: '#f8f9fa', border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#000000', width: '5%' }}>U</th>
                  <th style={{ backgroundColor: '#f8f9fa', border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#000000', width: '8%' }}>Q</th>
                  <th style={{ backgroundColor: '#f8f9fa', border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#000000', width: '16%' }}>P.U HT</th>
                  <th style={{ backgroundColor: '#f8f9fa', border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontWeight: 600, fontSize: '0.85rem', color: '#000000', width: '16%' }}>Montant</th>
                </tr>
              </thead>
              <tbody>
                {formData.items?.map((item, index) => {
                  const unitPrice = parseFloat(item.unitPrice) || 0;
                  const quantity = parseInt(item.quantity) || 1;
                  const total = item.total || (unitPrice * quantity);
                  const itemCode = generateItemCode(item, index);
                  
                  return (
                    <tr key={index}>
                      <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontSize: '0.8rem', color: '#000' }}>
                        {index + 1}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontSize: '0.8rem', color: '#000' }}>
                        {itemCode}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'left', fontSize: '0.8rem', color: '#000' }}>
                        <div>
                          <span style={{ fontWeight: 500, color: '#000' }}>{item.description}</span>
                          {item.productId && (
                            <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '0.2rem' }}>Réf: {item.productId}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontSize: '0.8rem', color: '#000' }}>
                        U
                      </td>
                      <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'center', fontSize: '0.8rem', color: '#000' }}>
                        {quantity}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'right', fontSize: '0.8rem', color: '#000' }}>
                        {formatCurrency(unitPrice)}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', textAlign: 'right', fontSize: '0.8rem', fontWeight: 500, color: '#000' }}>
                        {formatCurrency(total)}
                      </td>
                    </tr>
                  );
                })}
                {/* Add empty rows to fill space if needed */}
                {Array.from({ length: Math.max(0, 3 - (formData.items?.length || 0)) }, (_, i) => (
                  <tr key={`empty-${i}`}>
                    <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem', height: '2rem' }}>&nbsp;</td>
                    <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem' }}>&nbsp;</td>
                    <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem' }}>&nbsp;</td>
                    <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem' }}>&nbsp;</td>
                    <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem' }}>&nbsp;</td>
                    <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem' }}>&nbsp;</td>
                    <td style={{ border: '1px solid #000', padding: '0.5rem 0.3rem' }}>&nbsp;</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals Section */}
          <div className="totals-section" style={{ display: 'flex', justifyContent: 'flex-start', margin: '0.5rem 0 3rem 0', marginLeft: '52%', width: '48%', flexDirection: 'column' }}>
            <table className="totals-table" style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.85rem' }}>
              <tbody>
                {(() => {
                  const items = formData.items || [];
                  
                  // Calculate subtotal from items
                  const subtotal = items.reduce((sum, item) => {
                    if (item.total) {
                      return sum + parseFloat(item.total);
                    } else {
                      const unitPrice = parseFloat(item.unitPrice) || 0;
                      const quantity = parseInt(item.quantity) || 1;
                      return sum + (unitPrice * quantity);
                    }
                  }, 0);
                  
                  // Tax calculations
                  const tvaRate = parseFloat(formData.taxRate) || 19;
                  const tvaAmount = subtotal * (tvaRate / 100);
                  const totalTTC = subtotal + tvaAmount;
                  const netToPay = totalTTC;
                  
                  return (
                    <>
                      <tr key="total-ht">
                        <td className="total-label" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', backgroundColor: '#f8f9fa', fontWeight: 600, textAlign: 'left', color: '#000' }}>Total HT</td>
                        <td className="total-value" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 500, color: '#000' }}>{formatCurrency(subtotal)}</td>
                      </tr>
                      <tr key="total-tva">
                        <td className="total-label" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', backgroundColor: '#f8f9fa', fontWeight: 600, textAlign: 'left', color: '#000' }}>TVA ({tvaRate}%)</td>
                        <td className="total-value" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 500, color: '#000' }}>{formatCurrency(tvaAmount)}</td>
                      </tr>
                      <tr key="total-ttc">
                        <td className="total-label" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', backgroundColor: '#f8f9fa', fontWeight: 600, textAlign: 'left', color: '#000' }}>Total TTC</td>
                        <td className="total-value" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 500, color: '#000' }}>{formatCurrency(totalTTC)}</td>
                      </tr>
                      <tr key="net-total" className="net-total">
                        <td className="total-label" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', backgroundColor: '#e3f2fd', fontWeight: 700, textAlign: 'left', fontSize: '0.9rem', color: '#000' }}>Net à Payé</td>
                        <td className="total-value" style={{ border: '1px solid #000', padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 700, fontSize: '0.9rem', color: '#1e40af' }}>{formatCurrency(netToPay)}</td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
            
            {/* Amount in Words - directly under the totals table */}
            {(documentType.id === 'invoice' || documentType.id === 'purchase-order') && (
              <div className="amount-in-words" style={{ width: '100%', marginTop: '0.5rem', marginBottom: '1rem', textAlign: 'left' }}>
              <p style={{ margin: 0, padding: '0.5rem 0', fontWeight: 500, color: '#000', fontSize: '0.9rem', lineHeight: 1.4, wordWrap: 'break-word', overflowWrap: 'break-word', display: 'block', whiteSpace: 'normal' }}>
                <strong>Arrêté la présente {getDocumentTitle().toLowerCase()} à la somme de:</strong><br />
                <span style={{ textTransform: 'capitalize' }}>{numberToWords(Math.floor((formData.items || []).reduce((sum, item) => {
                  if (item.total) {
                    return sum + parseFloat(item.total);
                  } else {
                    const unitPrice = parseFloat(item.unitPrice) || 0;
                    const quantity = parseInt(item.quantity) || 1;
                    return sum + (unitPrice * quantity);
                  }
                }, 0) * 1.19))}</span> dinars algériens
              </p>
            </div>
            )}


          </div>



          

          {/* Simple Footer - Fixed at bottom */}
          <div style={{ position: 'absolute', bottom: '15mm', left: '15mm', right: '15mm', paddingTop: '1rem', width: 'calc(100% - 30mm)', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#666' }}>
              <p style={{ margin: 0, marginBottom: '0.25rem' }}>
                <strong style={{ color: '#1e40af' }}>{companySettings.companyName}</strong> - {companySettings.description} | {companySettings.address} | Tél: {companySettings.phone}
              </p>
              <p style={{ margin: 0 }}>
                RC: {companySettings.rcNumber} | NIF: {companySettings.nifNumber} | © {new Date().getFullYear()} Tous droits réservés
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;